/**
 * The team (or specialist) editor, for the portfolio and for a project's page. Name and purpose, the
 * members (each edited in groups, MemberEditor.tsx), the team's Advanced settings, and for the
 * portfolio where it is granted. A save carries no `ifVersion`: the platform merges field by field,
 * and says in `refused[]` what it kept its own value for and in `ignored[]` what it does not hold.
 */

import type { OrgTeamSettingsV2, TeamDelegation } from '@aioe/shared';
import { useId, useState } from 'react';

import { routes, type PortfolioTree, type TeamSaveAnswer } from '../../api/client.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, Tick } from '../ui.tsx';
import { RowMenu } from '../ui/RowMenu.tsx';
import { DelegationEditor } from './DelegationEditor.tsx';
import { MemberFace } from './MemberFace.tsx';
import { MemberEditor, type MemberLookups } from './MemberEditor.tsx';
import { blankMember, changeNote, delegationIncomplete, type EditableTeam, ignoredWords, originWords, PERMISSION_WORDS, refusedWords, saveErrorWords, slug, toBody } from './teamsModel.ts';

const NONE = '__none';

export type EditorWhere = { kind: 'portfolio'; tree: PortfolioTree } | { kind: 'project'; projectId: string };

type Draft = { name: string; description: string; lead: string; members: EditableTeam['members']; projects: string[]; pools: string; settings: OrgTeamSettingsV2; delegation?: TeamDelegation };

const toDraft = (t: EditableTeam | undefined): Draft =>
  t
    ? { name: t.name, description: t.description ?? '', lead: t.lead ?? '', members: t.members, projects: t.grants?.projects ?? [], pools: (t.grants?.pools ?? []).join(', '), settings: t.settings ?? {}, ...(t.delegation ? { delegation: t.delegation } : {}) }
    : { name: '', description: '', lead: '', members: [blankMember()], projects: [], pools: '', settings: {} };

const REVIEW_WORDS = { none: 'No review', self: 'The team reviews its own work', human: 'A person reviews first' } as const;
const CLEANUP_WORDS = { auto: 'Clean up automatically', manual: 'Leave for a person', prompt: 'Ask each time' } as const;

export function TeamEditor({ team, kind, where, lookups, projectName, onDone, onCancel }: { team?: EditableTeam; kind: 'team' | 'specialist'; where: EditorWhere; lookups: MemberLookups; projectName: (id: string) => string; onDone: () => void; onCancel: () => void }) {
  const ids = useId();
  const isSpecialist = (team?.kind ?? kind) === 'specialist';
  const word = isSpecialist ? 'specialist' : 'team';
  const [d, setD] = useState<Draft>(() => toDraft(team));
  const [error, setError] = useState<string>();
  const [after, setAfter] = useState<TeamSaveAnswer>();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDelegation, setShowDelegation] = useState(false);
  const [open, setOpen] = useState<number>(0);
  const projects = where.kind === 'portfolio' ? [...where.tree.areas.flatMap((a) => a.projects), ...where.tree.unfiled] : [];
  const setMember = (i: number, m: Partial<Draft['members'][number]>) => setD({ ...d, members: d.members.map((x, j) => (j === i ? { ...x, ...m } : x)) });
  const setSetting = (patch: Partial<OrgTeamSettingsV2>) => setD({ ...d, settings: { ...d.settings, ...patch } });
  const note = (path: string) => (team ? changeNote(team, path) : undefined);
  const origin = team ? originWords(team, projectName) : undefined;

  const save = async () => {
    setError(undefined);
    try {
      const body = toBody({
        ...d,
        kind: team?.kind ?? kind,
        ...(where.kind === 'portfolio' ? { grants: { projects: d.projects, pools: d.pools.split(',').map((x) => x.trim()).filter(Boolean) } } : {}),
      });
      const answer = where.kind === 'portfolio' ? await routes.saveTeam({ ...body, id: team?.id ?? slug(d.name) }, !team) : await routes.saveProjectTeam(where.projectId, team!.id, body);
      if (answer.refused?.length || answer.ignored?.length) setAfter(answer);
      else onDone();
    } catch (e) {
      setError(saveErrorWords(e));
    }
  };
  const remove = async () => {
    if (!team) return;
    setError(undefined);
    try {
      if (where.kind === 'portfolio') await routes.deleteTeam(team.id);
      else await routes.deleteProjectTeam(where.projectId, team.id);
      onDone();
    } catch (e) {
      setError(saveErrorWords(e));
    }
  };

  const num = (v: string) => (v.trim() && Number.isFinite(Number(v)) ? Math.max(1, Math.floor(Number(v))) : undefined);
  const s = d.settings;
  // The team's people, as delegation names them: its human members who name a person, and its owner.
  const people = [
    ...d.members.filter((m) => m.human && m.subjectId).map((m) => ({ id: m.subjectId!, name: m.name.trim() || 'A teammate' })),
    ...(team?.owner && !d.members.some((m) => m.human && m.subjectId === team.owner!.id) ? [{ id: team.owner.id, name: team.owner.name ? `${team.owner.name} (owner)` : 'The owner' }] : []),
  ];
  const delegationProblem = d.delegation ? delegationIncomplete(d.delegation) : undefined;
  const ready = d.name.trim() && d.members.length > 0 && d.members.every((m) => m.name.trim()) && !delegationProblem;

  if (after) {
    return (
      <ListGroup title={`Saved ${d.name}`} description="Saved, with what the platform kept as it was.">
        {refusedWords(after.refused ?? []).map((t) => (
          <ListBlock key={t} className="text-sm text-muted">
            {t} Someone changed it on the platform first, and that change stands.
          </ListBlock>
        ))}
        {after.ignored?.length ? <ListBlock className="text-sm text-muted">{ignoredWords(after.ignored)}</ListBlock> : null}
        <ListBlock className="flex justify-end">
          <Button size="sm" variant="primary" onPress={onDone}>
            Done
          </Button>
        </ListBlock>
      </ListGroup>
    );
  }

  /** A field's hint and its "last changed" note, as one muted line. */
  const hintOf = (hint: string | undefined, path?: string) => [hint, path ? note(path) : undefined].filter(Boolean).join(' · ') || undefined;
  const delegationSummary = [d.delegation?.topics.length ? `${d.delegation.topics.length} ${d.delegation.topics.length === 1 ? 'topic' : 'topics'}` : '', d.delegation?.cover.length ? `${d.delegation.cover.length} cover ${d.delegation.cover.length === 1 ? 'rule' : 'rules'}` : ''].filter(Boolean).join(', ');

  return (
    <>
      <ListGroup title={team ? `Edit ${team.name}` : `New ${word}`} description={origin}>
        <ListRow title="Name" htmlFor={`${ids}-name`} description={note('name')}>
          <input id={`${ids}-name`} className={`${inputClass} max-w-80`} value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder={isSpecialist ? 'Release manager' : 'Finance squad'} />
        </ListRow>
        <ListRow title="What it is for" htmlFor={`${ids}-purpose`} description={note('description')}>
          <input id={`${ids}-purpose`} className={inputClass} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder="Reporting and reconciliation work" />
        </ListRow>
      </ListGroup>

      <ListGroup title={isSpecialist ? 'The specialist' : 'Members'} count={isSpecialist ? undefined : d.members.length} description={isSpecialist ? 'Its role, runtime, instructions and where it runs.' : 'Each member and how it is set up. Open one to edit it.'}>
        {d.members.map((m, i) => (
          <ListRow
            key={i}
            icon={<MemberFace member={m} index={i} />}
            title={m.name || `Member ${i + 1}`}
            description={[m.role, d.lead === m.name && m.name ? 'Leads the team' : m.human ? 'A person' : ''].filter(Boolean).join(' · ') || undefined}
            control={
              <RowMenu
                label={`More for ${m.name || `member ${i + 1}`}`}
                items={[{ id: 'edit', label: open === i ? 'Close the editor' : 'Edit' }, !isSpecialist && d.members.length > 1 && { id: 'remove', label: `Remove from the ${word}`, danger: true }]}
                onAction={(id) => (id === 'edit' ? setOpen(open === i ? -1 : i) : setD({ ...d, members: d.members.filter((_, j) => j !== i), lead: d.lead === m.name ? '' : d.lead }))}
              />
            }
            compactControl
          >
            {open === i ? <MemberEditor member={m} onChange={(p) => setMember(i, p)} lookups={lookups} teamId={team?.id} isLead={d.lead === m.name && !!m.name} note={(f) => note(`members.${m.name}.${f}`)} /> : null}
          </ListRow>
        ))}
        {!isSpecialist && (
          <ListRow
            title="Lead"
            description="The agent the rest take their work from."
            control={<ChoiceSelect label="Lead" value={d.lead || NONE} options={{ [NONE]: 'No lead', ...Object.fromEntries(d.members.filter((m) => m.name.trim() && !m.human).map((m) => [m.name, `${m.name} leads`])) }} onChange={(v) => setD({ ...d, lead: v === NONE ? '' : v })} />}
          />
        )}
        {!isSpecialist && (
          <ListBlock>
            <Button
              size="sm"
              variant="secondary"
              onPress={() => {
                setD({ ...d, members: [...d.members, blankMember()] });
                setOpen(d.members.length);
              }}
            >
              Add a member
            </Button>
          </ListBlock>
        )}
      </ListGroup>

      {!isSpecialist && (
        <ListGroup
          title="Advanced settings"
          description="Review, permissions, worktrees, budgets and guidelines for the whole team. Each is the workbench's default until set."
          actions={
            <Button size="sm" variant="ghost" aria-expanded={showAdvanced} onPress={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Hide' : 'Show'}
            </Button>
          }
        >
          {showAdvanced ? (
            <>
              <ListRow title="Review" description={note('settings.reviewPolicy')} control={<ChoiceSelect label="Review policy" value={s.reviewPolicy ?? NONE} options={{ [NONE]: "The workbench's default", ...REVIEW_WORDS }} onChange={(v) => setSetting({ reviewPolicy: v === NONE ? undefined : (v as OrgTeamSettingsV2['reviewPolicy']) })} />} />
              <ListRow title="Permission mode" description={hintOf('What a member does without asking, unless its own setting says otherwise.', 'settings.permissionMode')} control={<ChoiceSelect label="Team permission mode" value={s.permissionMode ?? NONE} options={{ [NONE]: "The workbench's default", ...PERMISSION_WORDS } as Record<string, string>} onChange={(v) => setSetting({ permissionMode: v === NONE ? undefined : (v as OrgTeamSettingsV2['permissionMode']) })} />} />
              <ListRow title="Worktrees" description={note('settings.worktreeCleanup')} control={<ChoiceSelect label="Worktree clean-up" value={s.worktreeCleanup ?? NONE} options={{ [NONE]: "The workbench's default", ...CLEANUP_WORDS }} onChange={(v) => setSetting({ worktreeCleanup: v === NONE ? undefined : (v as OrgTeamSettingsV2['worktreeCleanup']) })} />} />
              <ListRow title="Plans at once" htmlFor={`${ids}-plans`} description={note('settings.maxConcurrentPlans')} control={<input id={`${ids}-plans`} className={`${inputClass} w-32`} inputMode="numeric" value={s.maxConcurrentPlans ?? ''} onChange={(e) => setSetting({ maxConcurrentPlans: num(e.target.value) })} placeholder="Default" />} />
              <ListRow title="Token budget per task" htmlFor={`${ids}-budget`} description={note('settings.taskTokenBudget')} control={<input id={`${ids}-budget`} className={`${inputClass} w-40`} inputMode="numeric" value={s.taskTokenBudget ?? ''} onChange={(e) => setSetting({ taskTokenBudget: num(e.target.value) })} placeholder="No budget" />} />
              <ListRow title="Guidelines" htmlFor={`${ids}-guidelines`} description={hintOf(`How the team works, in a few lines. ${(s.guidelines ?? '').length} of 2,000 characters.`, 'settings.guidelines')}>
                <textarea id={`${ids}-guidelines`} className={`${inputClass} resize-y`} rows={4} maxLength={2000} value={s.guidelines ?? ''} onChange={(e) => setSetting({ guidelines: e.target.value || undefined })} />
              </ListRow>
              {(s.orchestration || s.approvals || s.research) && <ListRow title="Also set" description="Orchestration, approvals and research are kept exactly as the workbench has them." value={[s.orchestration && 'Orchestration', s.approvals && 'Approvals', s.research && 'Research'].filter(Boolean).join(', ')} />}
            </>
          ) : (
            <ListBlock className="text-xs text-muted">Hidden. Show them to change how the team reviews, asks and cleans up.</ListBlock>
          )}
        </ListGroup>
      )}

      {!isSpecialist && (
        <ListGroup
          title="Delegation"
          count={delegationSummary || undefined}
          description={`Who the team's agents ask, and who answers for whom.${note('delegation') ? ` ${note('delegation')}` : ''}`}
          actions={
            <Button size="sm" variant="ghost" aria-expanded={showDelegation} onPress={() => setShowDelegation(!showDelegation)}>
              {showDelegation ? 'Hide' : 'Show'}
            </Button>
          }
        >
          {showDelegation ? (
            <ListBlock className="grid min-w-0 gap-3">
              <DelegationEditor value={d.delegation ?? { topics: [], cover: [] }} people={people} onChange={(delegation) => setD({ ...d, delegation })} />
              {delegationProblem ? <p className="m-0 text-xs text-muted">{delegationProblem}</p> : null}
            </ListBlock>
          ) : (
            <ListBlock className="text-xs text-muted">{delegationProblem ?? (delegationSummary ? `Set: ${delegationSummary}.` : 'None yet: questions go to whoever started the work.')}</ListBlock>
          )}
        </ListGroup>
      )}

      {where.kind === 'portfolio' && (
        <ListGroup title="Where it is granted" description="The projects that may use it, and the nodes its agents may run on.">
          <ListBlock>
            <fieldset className="m-0 grid gap-1 border-0 p-0 text-sm">
              <legend className="mb-1 text-sm font-medium">Granted to projects</legend>
              {projects.length === 0 ? (
                <span className="text-muted">No enterprise project yet.</span>
              ) : (
                projects.map((p) => (
                  <Tick key={p.id} checked={d.projects.includes(p.id)} onChange={(v) => setD({ ...d, projects: v ? [...d.projects, p.id] : d.projects.filter((x) => x !== p.id) })}>
                    {p.name}
                  </Tick>
                ))
              )}
            </fieldset>
          </ListBlock>
          <ListRow title="Compute pools, as node tags" htmlFor={`${ids}-pools`} description="Separated by commas.">
            <input id={`${ids}-pools`} className={inputClass} value={d.pools} onChange={(e) => setD({ ...d, pools: e.target.value })} placeholder="tag:gpu, tag:finance" />
          </ListRow>
        </ListGroup>
      )}

      <div className="grid gap-3">
        <ErrorNote error={error} />
        <div className="flex flex-wrap items-center gap-2">
          {team && (
            <Button size="sm" variant="danger-soft" onPress={() => void remove()}>
              Delete {word}
            </Button>
          )}
          <span className="flex-1" />
          <Button size="sm" variant="secondary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isDisabled={!ready} onPress={() => void save()}>
            {team ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </>
  );
}
